"use client"

import { Button } from "@preview-kit/ui/components/button"
import { Card } from "@preview-kit/ui/components/card"
import { cn } from "@preview-kit/ui/lib/utils"
import { Check, Copy } from "lucide-react"
import { useState } from "react"

interface CodeSnippetProps {
	code: string
	language?: string
	title?: string
	className?: string
	showLineNumbers?: boolean
}

export function CodeSnippet({
	code,
	language = "javascript",
	title,
	className,
	showLineNumbers = false,
}: CodeSnippetProps) {
	const [copied, setCopied] = useState(false)

	const copyToClipboard = async () => {
		try {
			await navigator.clipboard.writeText(code)
			setCopied(true)
			setTimeout(() => setCopied(false), 2000)
		} catch (err) {
			console.error("Failed to copy code:", err)
		}
	}

	const lines = code.split("\n")

	return (
		<div className={cn("w-full overflow-hidden", className)}>
			<Card className="overflow-hidden py-0 my-0 w-full">
				{title && (
					<div className="flex items-center justify-between px-4 py-2 bg-muted border-b">
						<div className="flex items-center gap-2 min-w-0 flex-1">
							<span className="text-sm font-medium truncate">{title}</span>
							{language && (
								<span className="text-xs text-muted-foreground bg-background px-2 py-1 rounded flex-shrink-0">
									{language}
								</span>
							)}
						</div>
						<Button
							size="sm"
							onClick={copyToClipboard}
							className="h-8 w-8 p-0 flex-shrink-0"
						>
							{copied ? (
								<Check className="h-4 w-4 text-green-500" />
							) : (
								<Copy className="h-4 w-4" />
							)}
						</Button>
					</div>
				)}

				<div className="relative w-full">
					{!title && (
						<Button
							variant="ghost"
							size="sm"
							onClick={copyToClipboard}
							className="absolute top-2 right-2 h-8 w-8 p-0 z-10  bg-gray-50 hover:bg-gray-100"
						>
							{copied ? (
								<Check className="h-4 w-4 text-green-500" />
							) : (
								<Copy className="h-4 w-4" />
							)}
						</Button>
					)}

					<div className="overflow-x-auto w-full bg-muted/30">
						<pre className="p-4 text-xs font-mono select-text min-w-0 bg-transparent">
							<code className="block select-text whitespace-pre">
								{showLineNumbers
									? lines.map((line, index) => (
											<div key={index} className="flex">
												<span className="text-muted-foreground mr-4 text-right w-8 select-none flex-shrink-0">
													{index + 1}
												</span>
												<span className="select-text whitespace-pre">
													{line}
												</span>
											</div>
										))
									: code}
							</code>
						</pre>
					</div>
				</div>
			</Card>
		</div>
	)
}
